import React from 'react';

const DEFAULT_CUE_MS = 600;
const DEFAULT_WINDOW_MS = 3000;

const RULE_LABELS = {
  parity: 'Odd or even?',
  magnitude: 'Less or greater than 5?',
};

export function SwitchCueView({ stimulus, compact = false }) {
  return (
    <div
      style={{
        minWidth: compact ? 96 : 220,
        padding: compact ? 'var(--space-2)' : 'var(--space-3) var(--space-4)',
        borderRadius: compact ? 'var(--radius-control)' : 'var(--radius-md)',
        border: '1px solid var(--border-strong)',
        background: 'var(--bg-elevated)',
        color: 'var(--text-primary)',
        fontSize: compact ? 9 : 'var(--text-body-size)',
        fontWeight: 750,
        lineHeight: 1.2,
        letterSpacing: '0.01em',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {RULE_LABELS[stimulus?.cue] || 'Use the shown rule'}
    </div>
  );
}

export function SwitchDigitView({ stimulus, compact = false }) {
  return (
    <span
      style={{
        fontSize: compact ? 36 : 'clamp(88px, 20vw, 168px)',
        fontWeight: 800,
        lineHeight: 0.9,
        letterSpacing: '-0.04em',
        color: 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {stimulus?.digit}
    </span>
  );
}

// Both rules map onto the SAME two keys, which is what makes changing rule
// costly and is the whole point of the paradigm. Each label names both rules'
// halves so the mapping never has to be recalled — the cue selects which half
// applies. A digit alone therefore never determines the answer: 3 is odd AND
// low, 7 is odd AND high.
const targets = [
  {
    id: 'left',
    label: 'Odd / less than 5',
    keys: ['ArrowLeft', 'f'],
    hint: 'F · ←',
  },
  {
    id: 'right',
    label: 'Even / greater than 5',
    keys: ['ArrowRight', 'j'],
    hint: 'J · →',
  },
];

// PRACTICE ONLY, gated by the runner on trial.isPractice. Both the cue and the
// digit are on screen and applying the rule IS the task, so nothing withheld is
// computed here. It reads only the stimulus it is handed: the runner also
// offers a context carrying the previous trial's stimulus, and deriving
// anything from that would classify the trial relative to its predecessor —
// which the client is never told, by design, because telling the participant a
// rule change is coming is exactly the preparation being measured.
const practiceExpectation = (stimulus) => {
  if (stimulus?.cue === 'parity') return stimulus.digit % 2 === 0 ? 'right' : 'left';
  if (stimulus?.cue === 'magnitude') return stimulus.digit < 5 ? 'left' : 'right';
  return null;
};

// The cue is shown ALONE for cue_stimulus_interval_ms, then the digit appears
// and the clock starts AT THE DIGIT. Measuring from the cue folds the whole
// preparation interval into every reaction time and destroys the flexibility
// cost, which is the entire measurement. The cue then stays visible
// (cuePersists is left at its default) because this is not a memory task — the
// participant must never have to recall which rule applies.
//
// Number() coercion is load-bearing: the RT ceiling is windowMs + 250, so a
// string window would concatenate instead of add and every response would be
// accepted.
const timing = (params) => ({
  fixationMs: 300,
  cueMs: Number(params?.cue_stimulus_interval_ms) || DEFAULT_CUE_MS,
  windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
  // Deliberately short, and identical on every trial. Practice and scored
  // trials are ONE CONTINUOUS STREAM: the server defines each trial against
  // its PREDECESSOR, so scored trial 4 takes its predecessor from practice
  // trial 3. A pause, an interstitial, or a longer gap at that boundary
  // invalidates the first scored trial.
  gapMs: 200,
});

export const cuedSwitchTask = {
  id: 'cued-switch',
  name: 'Rule Switch',
  pillar: 'flexibility',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'A rule appears before each digit and stays visible.',
    'For odd or even: left is odd, right is even.',
    'For less or greater than 5: left is less, right is greater.',
    'The same two keys answer both rules — read the rule every time.',
  ],
  targets,
  examples: [
    {
      stimulus: { digit: 7, cue: 'parity' },
      response: 'Left',
      note: '7 is odd',
    },
    {
      stimulus: { digit: 8, cue: 'magnitude' },
      response: 'Right',
      note: '8 is greater than 5',
    },
  ],
  toAnswer: (targetId) => ({ response: targetId }),
  timing,
  practiceExpectation,
  CueView: SwitchCueView,
  StimulusView: SwitchDigitView,
};
